import type {
  DailySummaryResponse,
  ForecastResponse,
  Insight,
  LeagueBoard,
  TargetsResponse,
} from "@/lib/dashApi";
import { fmtCurrency, fmtNumber, fmtPct, fmtValue } from "@/lib/format";
import { EmptyState, Panel, SourceBadge } from "@/components/dash/ui";

const SEV: Record<string, { ring: string; text: string; dot: string }> = {
  critical: { ring: "ring-rose-500/30 bg-rose-500/5", text: "text-rose-300", dot: "bg-rose-400" },
  warning: { ring: "ring-amber-500/30 bg-amber-500/5", text: "text-amber-300", dot: "bg-amber-400" },
  positive: { ring: "ring-emerald-500/30 bg-emerald-500/5", text: "text-emerald-300", dot: "bg-emerald-400" },
  info: { ring: "ring-sky-500/30 bg-sky-500/5", text: "text-sky-300", dot: "bg-sky-400" },
};

function dirArrow(d: string): string {
  return d === "up" ? "▲" : d === "down" ? "▼" : "→";
}

export function InsightsStrip({ insights }: { insights: Insight[] }) {
  if (!insights.length) return <EmptyState compact>No notable changes detected.</EmptyState>;
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
      {insights.map((i) => {
        const s = SEV[i.severity] ?? SEV.info;
        return (
          <div key={i.code} className={`rounded-xl p-4 ring-1 ${s.ring}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2">
                <span className={`text-sm ${s.text}`}>{dirArrow(i.direction)}</span>
                <p className="text-sm font-semibold text-slate-100">{i.title}</p>
              </div>
              {i.source && <SourceBadge code={i.source} />}
            </div>
            <p className="mt-1 text-xs text-slate-400">{i.detail}</p>
          </div>
        );
      })}
    </div>
  );
}

export function MovementBadge({ movement }: { movement: number | null }) {
  if (movement === null) return <span className="text-[11px] text-slate-500">new</span>;
  if (movement === 0) return <span className="text-[11px] text-slate-500">→ 0</span>;
  const up = movement > 0;
  return (
    <span className={`text-[11px] font-medium ${up ? "text-emerald-400" : "text-rose-400"}`}>
      {up ? "▲" : "▼"} {Math.abs(movement)}
    </span>
  );
}

export function LeagueTableCard({ board }: { board: LeagueBoard }) {
  return (
    <Panel title={board.label} right={<SourceBadge code={board.source} />}>
      {!board.rows.length ? (
        <EmptyState compact>No data.</EmptyState>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500">
              <th className="pb-2 font-medium">#</th>
              <th className="pb-2 font-medium">Name</th>
              <th className="pb-2 text-right font-medium">Value</th>
              <th className="pb-2 text-right font-medium">Move</th>
              <th className="pb-2 text-right font-medium">Trend</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-500/40">
            {board.rows.map((r) => (
              <tr key={`${board.key}-${r.rank}-${r.name}`} className="text-slate-300">
                <td className="py-2 font-semibold text-slate-500">{r.rank}</td>
                <td className="py-2">{r.name}</td>
                <td className="py-2 text-right tabular-nums text-white">{fmtValue(r.value, board.unit === "pct" ? "count" : board.unit)}{board.unit === "pct" ? "%" : ""}</td>
                <td className="py-2 text-right"><MovementBadge movement={r.movement} /></td>
                <td className="py-2 text-right text-[11px] text-slate-400">{r.trend_pct === null ? "—" : fmtPct(r.trend_pct)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Panel>
  );
}

export function TargetsRow({ data }: { data: TargetsResponse }) {
  if (!data.targets.length) {
    return <EmptyState compact>No KPI targets configured (set IRONDASH_TARGET_* env vars).</EmptyState>;
  }
  return (
    <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
      {data.targets.map((t) => {
        const pct = t.pct_achieved ?? 0;
        const good = t.on_track;
        return (
          <div key={t.key} className="panel panel-pad">
            <div className="flex items-center justify-between">
              <p className="text-xs font-medium uppercase tracking-wide text-slate-400">{t.label}</p>
              {t.source && <SourceBadge code={t.source} />}
            </div>
            <p className={`mt-1 text-xl font-semibold ${good ? "text-emerald-300" : "text-amber-300"}`}>
              {fmtValue(t.actual, t.unit === "EUR" ? "EUR" : "count")}
              {t.unit === "hours" ? "h" : ""}
            </p>
            <p className="text-[11px] text-slate-500">
              target {fmtValue(t.target, t.unit === "EUR" ? "EUR" : "count")}{t.unit === "hours" ? "h" : ""} · {pct}%
            </p>
            <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-ink-600">
              <div className={`h-full rounded-full ${good ? "bg-emerald-500" : "bg-amber-500"}`} style={{ width: `${Math.min(100, Math.max(3, pct))}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function ForecastRow({ data }: { data: ForecastResponse }) {
  if (!data.series.length) return <EmptyState compact>Not enough data to project.</EmptyState>;
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
      {data.series.map((f) => {
        const up = (f.growth_pct ?? 0) >= 0;
        return (
          <div key={f.key} className="panel panel-pad">
            <div className="flex items-center justify-between">
              <p className="text-xs font-medium uppercase tracking-wide text-slate-400">{f.label}</p>
              <SourceBadge code={f.source} />
            </div>
            <p className="mt-1 text-xl font-semibold text-white">
              {fmtValue(f.projected_next, f.unit)} <span className="text-xs font-normal text-slate-500">projected</span>
            </p>
            <p className={`text-[11px] ${up ? "text-emerald-400" : "text-rose-400"}`}>
              {f.growth_pct === null ? "—" : `${up ? "▲" : "▼"} ${fmtPct(f.growth_pct)}`} · now {fmtValue(f.current, f.unit)}
            </p>
          </div>
        );
      })}
    </div>
  );
}

function DailyDelta({ d }: { d: { value: number | null; pct: number | null } }) {
  if (d.pct === null) return <span className="text-slate-600">—</span>;
  const up = d.pct >= 0;
  return <span className={up ? "text-emerald-400" : "text-rose-400"}>{up ? "▲" : "▼"} {fmtPct(d.pct)}</span>;
}

const DAILY_ROWS: { key: string; label: string; unit: string }[] = [
  { key: "sales", label: "Sales", unit: "EUR" },
  { key: "repairs_completed", label: "Repairs completed", unit: "count" },
  { key: "repairs_new", label: "Repairs new", unit: "count" },
  { key: "support_new", label: "New conversations", unit: "count" },
  { key: "subscriptions_new", label: "New subscriptions", unit: "count" },
];

export function DailySummaryBlock({ data }: { data: DailySummaryResponse }) {
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500">
          <th className="pb-2 font-medium">Metric</th>
          <th className="pb-2 text-right font-medium">Today</th>
          <th className="pb-2 text-right font-medium">vs yesterday</th>
          <th className="pb-2 text-right font-medium">vs last week</th>
          <th className="pb-2 text-right font-medium">vs last month</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-ink-500/40">
        {DAILY_ROWS.map((row) => {
          const m = data.metrics[row.key];
          if (!m) return null;
          return (
            <tr key={row.key} className="text-slate-300">
              <td className="py-2">{row.label}</td>
              <td className="py-2 text-right tabular-nums text-white">
                {row.unit === "EUR" ? fmtCurrency(m.today) : fmtNumber(m.today)}
              </td>
              <td className="py-2 text-right text-xs"><DailyDelta d={m.vs_yesterday} /></td>
              <td className="py-2 text-right text-xs"><DailyDelta d={m.vs_last_week} /></td>
              <td className="py-2 text-right text-xs"><DailyDelta d={m.vs_last_month} /></td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
